import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const EMPTY = { name: "", deadline_days: 0, min_duration: 0.5, max_duration: 30, emergency_after_deadline: false, annual_entitlement: 12, accrual: "monthly", carry_forward: false, monthly_accrual: null };

const STATUS_LABELS = { pending: "Pending with HOD", hod_approved: "HOD approved · awaiting admin" };

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={
    status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
    : status === "hod_approved" ? "bg-blue-50 text-[#002FA7] border-blue-200"
    : "bg-amber-50 text-amber-700 border-amber-200"}>
    {STATUS_LABELS[status] || status}
  </Badge>
);

function TypeDialog({ editing, onClose }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => {
    const base = editing ? { ...EMPTY, ...editing } : { ...EMPTY };
    if (!Array.isArray(base.monthly_accrual) || base.monthly_accrual.length !== 12) {
      base.monthly_accrual = Array(12).fill(Math.round((Number(base.annual_entitlement) / 12) * 100) / 100);
    }
    return base;
  });
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form,
        deadline_days: Number(form.deadline_days), min_duration: Number(form.min_duration),
        max_duration: Number(form.max_duration), annual_entitlement: Number(form.annual_entitlement),
        monthly_accrual: form.accrual === "monthly" ? form.monthly_accrual.map((x) => Number(x) || 0) : null };
      if (editing) {
        await api.put(`/leave/types/${editing.id}`, payload);
        toast.success("Leave type updated");
      } else {
        await api.post("/leave/types", payload);
        toast.success("Leave type created");
      }
      setOpen(false);
      onClose?.();
      queryClient.invalidateQueries({ queryKey: ["leave-types"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const Num = ({ label, name, step = 1, testid }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="number" step={step} min="0" required data-testid={testid} value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
    </div>
  );

  const Toggle = ({ label, name, testid }) => (
    <div className="flex items-center gap-3">
      <Switch checked={form[name]} data-testid={testid} onCheckedChange={(v) => setForm({ ...form, [name]: v })} />
      <Label>{label}</Label>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) onClose?.(); }}>
      <DialogTrigger asChild>
        {editing ? (
          <Button variant="ghost" size="icon" data-testid={`edit-leave-type-${editing.id}`}
            className="text-slate-400 hover:text-slate-950 hover:bg-slate-100">
            <Pencil className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" data-testid="add-leave-type-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4" /> Add leave type
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white rounded-md max-w-xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit leave type" : "Add leave type"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type of leave</Label>
            <Input required data-testid="leave-type-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Casual Leave" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Num label="Application deadline (days before leave)" name="deadline_days" testid="leave-type-deadline-input" />
            <Num label="Annual entitlement (days)" name="annual_entitlement" step="0.5" testid="leave-type-entitlement-input" />
            <Num label="Minimum duration (days)" name="min_duration" step="0.5" testid="leave-type-min-input" />
            <Num label="Maximum duration (days)" name="max_duration" step="0.5" testid="leave-type-max-input" />
          </div>
          <div className="space-y-1.5">
            <Label>How leave is earned</Label>
            <Select value={form.accrual} onValueChange={(v) => setForm({ ...form, accrual: v })}>
              <SelectTrigger data-testid="leave-type-accrual-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Month-wise (set January–December credits below)</SelectItem>
                <SelectItem value="yearly">Full entitlement at the start of the year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.accrual === "monthly" && (
            <div className="space-y-1.5" data-testid="monthly-accrual-grid">
              <Label>Month-wise accrual (days credited each month)</Label>
              <div className="grid grid-cols-4 gap-2">
                {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                  <div key={m} className="space-y-0.5">
                    <p className="text-xs text-slate-400">{m}</p>
                    <Input type="number" step="0.5" min="0" data-testid={`accrual-month-${i}`}
                      value={form.monthly_accrual[i]}
                      onChange={(e) => {
                        const next = [...form.monthly_accrual];
                        next[i] = e.target.value;
                        setForm({ ...form, monthly_accrual: next });
                      }} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 font-data">
                Total: {form.monthly_accrual.reduce((s, x) => s + (Number(x) || 0), 0)} days/year · accrues from the teacher's month of joining
              </p>
            </div>
          )}
          <Toggle label="Emergency leave allowed after deadline" name="emergency_after_deadline" testid="leave-type-emergency-toggle" />
          <Toggle label="Unused leave carries forward to next year" name="carry_forward" testid="leave-type-carryforward-toggle" />
          <Button type="submit" data-testid="leave-type-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
            {editing ? "Save changes" : "Create leave type"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LeaveAdmin() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const readOnly = user?.role !== "admin"; // HOD: review own-department applications, no editing
  const isHodReviewer = user?.role === "teacher" && !!user?.is_hod;
  const [statusFilter, setStatusFilter] = useState("pending");
  const [balDept, setBalDept] = useState("all");
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocForm, setAllocForm] = useState({});
  const [editAlloc, setEditAlloc] = useState(null);
  const [editApp, setEditApp] = useState(null);
  const [deleteAppId, setDeleteAppId] = useState(null);

  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: async () => (await api.get("/leave/types")).data });
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/admin/departments")).data });
  const { data: teachers = [] } = useQuery({ queryKey: ["teachers"], queryFn: async () => (await api.get("/admin/teachers")).data });
  const { data: balances = [] } = useQuery({
    queryKey: ["leave-balances", balDept],
    queryFn: async () => (await api.get("/leave/admin/summary", { params: balDept === "all" ? {} : { department_id: balDept } })).data,
  });
  const { data: apps = [] } = useQuery({
    queryKey: ["leave-applications", statusFilter],
    queryFn: async () => (await api.get("/leave/applications", { params: statusFilter === "all" ? {} : { status: statusFilter } })).data,
  });

  const removeType = async (id) => {
    await api.delete(`/leave/types/${id}`);
    toast.success("Leave type deleted");
    queryClient.invalidateQueries({ queryKey: ["leave-types"] });
  };

  const decide = async (id, action) => {
    try {
      await api.post(`/leave/applications/${id}/decide`, { action });
      toast.success(`Application ${action}d`);
      queryClient.invalidateQueries({ queryKey: ["leave-applications"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const hodDecide = async (id, action) => {
    try {
      await api.post(`/leave/applications/${id}/hod-decide`, { action });
      toast.success(action === "approve" ? "Forwarded to admin for final approval" : "Application rejected");
      queryClient.invalidateQueries({ queryKey: ["leave-applications"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveAlloc = async (payload) => {
    try {
      await api.put("/leave/admin/allocations", payload);
      toast.success("Allocation saved");
      setAllocOpen(false);
      setEditAlloc(null);
      queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeAlloc = async (row) => {
    try {
      await api.delete(`/leave/admin/allocations/${row.teacher_id}/${row.leave_type_id}`);
      toast.success("Allocation removed — the teacher no longer sees this leave type");
      queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveApp = async () => {
    try {
      await api.put(`/leave/applications/${editApp.id}`, {
        from_date: editApp.from_date, to_date: editApp.to_date,
        duration: Number(editApp.duration), reason: editApp.reason,
      });
      toast.success("Application updated");
      setEditApp(null);
      queryClient.invalidateQueries({ queryKey: ["leave-applications"] });
      queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delApp = async () => {
    try {
      await api.delete(`/leave/applications/${deleteAppId}`);
      toast.success("Application deleted");
      setDeleteAppId(null);
      queryClient.invalidateQueries({ queryKey: ["leave-applications"] });
      queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout title="Leave Management">
      <Tabs defaultValue="types">
        <TabsList className="bg-white border border-slate-200" data-testid="leave-admin-tabs">
          <TabsTrigger value="types" data-testid="tab-leave-types">Leave Types</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-leave-applications">Applications</TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-leave-balances">Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="types" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="leave-types-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Leave types</h2>
              {!readOnly && <TypeDialog />}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Deadline (days)</TableHead>
                  <TableHead className="text-right">Min–Max days</TableHead>
                  <TableHead className="text-right">Entitlement/yr</TableHead>
                  <TableHead>Earned</TableHead>
                  <TableHead>Emergency override</TableHead>
                  <TableHead>Carry forward</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8" data-testid="leave-types-empty">
                    No leave types yet. Create one to let teachers apply.
                  </TableCell></TableRow>
                )}
                {types.map((t) => (
                  <TableRow key={t.id} data-testid={`leave-type-row-${t.id}`}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-right font-data">{t.deadline_days}</TableCell>
                    <TableCell className="text-right font-data">{t.min_duration}–{t.max_duration}</TableCell>
                    <TableCell className="text-right font-data">{t.annual_entitlement}</TableCell>
                    <TableCell>{t.accrual === "monthly" ? "Monthly" : "Year start"}</TableCell>
                    <TableCell>{t.emergency_after_deadline ? "Yes" : "No"}</TableCell>
                    <TableCell>{t.carry_forward ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {!readOnly && (
                        <div className="flex gap-1">
                          <TypeDialog editing={t} />
                          <Button variant="ghost" size="icon" data-testid={`delete-leave-type-${t.id}`} onClick={() => removeType(t.id)}
                            className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="leave-apps-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Leave applications</h2>
              <div className="w-44">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending (HOD review)</SelectItem>
                    <SelectItem value="hod_approved">Forwarded to admin</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8" data-testid="leave-apps-empty">
                    No {statusFilter === "all" ? "" : statusFilter} applications.
                  </TableCell></TableRow>
                )}
                {apps.map((a) => (
                  <TableRow key={a.id} data-testid={`leave-app-row-${a.id}`}>
                    <TableCell className="font-medium">{a.teacher_name}</TableCell>
                    <TableCell>{a.leave_type_name}{a.emergency && (
                      <Badge variant="outline" className="ml-2 bg-red-50 text-red-700 border-red-200">Emergency</Badge>
                    )}</TableCell>
                    <TableCell className="font-data text-sm">{a.from_date} → {a.to_date}</TableCell>
                    <TableCell className="text-right font-data">{a.duration}</TableCell>
                    <TableCell className="max-w-56 truncate text-slate-500" title={a.reason}>{a.reason}</TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {a.status === "pending" && isHodReviewer && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`hod-approve-leave-${a.id}`} onClick={() => hodDecide(a.id, "approve")}
                              title="Approve and forward to admin"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" data-testid={`hod-reject-leave-${a.id}`} onClick={() => hodDecide(a.id, "reject")}
                              className="text-red-700 border-red-300 hover:bg-red-50">
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(a.status === "pending" || a.status === "hod_approved") && !readOnly && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`approve-leave-${a.id}`} onClick={() => decide(a.id, "approve")}
                              title="Final approval"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" data-testid={`reject-leave-${a.id}`} onClick={() => decide(a.id, "reject")}
                              className="text-red-700 border-red-300 hover:bg-red-50">
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {!readOnly && (
                          <>
                            <Button variant="ghost" size="icon" data-testid={`edit-leave-app-${a.id}`}
                              onClick={() => setEditApp({ ...a, duration: String(a.duration) })}
                              className="text-slate-400 hover:text-slate-950 hover:bg-slate-100">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" data-testid={`delete-leave-app-${a.id}`}
                              onClick={() => setDeleteAppId(a.id)}
                              className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={!!editApp} onOpenChange={() => setEditApp(null)}>
            <DialogContent className="bg-white rounded-md" data-testid="edit-leave-app-dialog">
              <DialogHeader><DialogTitle>Edit application — {editApp?.teacher_name}</DialogTitle></DialogHeader>
              {editApp && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>From date</Label>
                      <Input type="date" data-testid="edit-app-from" value={editApp.from_date}
                        onChange={(e) => setEditApp({ ...editApp, from_date: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>To date</Label>
                      <Input type="date" data-testid="edit-app-to" value={editApp.to_date}
                        onChange={(e) => setEditApp({ ...editApp, to_date: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Duration (days)</Label>
                    <Input type="number" step="0.5" min="0.5" data-testid="edit-app-duration" value={editApp.duration}
                      onChange={(e) => setEditApp({ ...editApp, duration: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Reason</Label>
                    <Input data-testid="edit-app-reason" value={editApp.reason}
                      onChange={(e) => setEditApp({ ...editApp, reason: e.target.value })} /></div>
                  <Button onClick={saveApp} data-testid="edit-app-save-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                    Save changes</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <AlertDialog open={!!deleteAppId} onOpenChange={() => setDeleteAppId(null)}>
            <AlertDialogContent className="bg-white rounded-md" data-testid="delete-leave-app-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this leave application?</AlertDialogTitle>
                <AlertDialogDescription>The teacher's balance will be recalculated if it was approved.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-app-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-app-confirm" onClick={delApp}
                  className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="balances" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="leave-balances-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Teacher leave balances</h2>
                <p className="text-sm text-slate-500 mt-0.5">Only allocated leave types appear here — and on the teacher's own dashboard.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-52">
                  <Select value={balDept} onValueChange={setBalDept}>
                    <SelectTrigger data-testid="leave-balance-dept-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All departments</SelectItem>
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" data-testid="allocate-leave-btn"
                  onClick={() => { setAllocForm({ teacher_id: "", leave_type_id: "", entitlement: "", brought_forward: 0 }); setAllocOpen(true); }}
                  className={`bg-slate-950 hover:bg-slate-800 text-white ${readOnly ? "hidden" : ""}`}>
                  <Plus className="h-4 w-4" /> Allocate leave
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">Sl. No.</TableHead>
                  <TableHead>Name of Teacher</TableHead>
                  <TableHead>Leave Type</TableHead>
                  <TableHead className="text-right">Entitlement</TableHead>
                  <TableHead className="text-right">Brought Forward</TableHead>
                  <TableHead className="text-right">Earned</TableHead>
                  <TableHead className="text-right">Availed</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Balance Carried Forward</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8" data-testid="leave-balances-empty">
                    No allocations yet — use "Allocate leave" to give a teacher a leave type.
                  </TableCell></TableRow>
                )}
                {balances.map((r, i) => (
                  <TableRow key={`${r.teacher_id}-${r.leave_type_id}`} data-testid={`leave-balance-row-${i}`}>
                    <TableCell className="text-right font-data">{i + 1}</TableCell>
                    <TableCell className="font-medium">{r.teacher_name}</TableCell>
                    <TableCell>{r.leave_type}</TableCell>
                    <TableCell className="text-right font-data">{r.entitlement}</TableCell>
                    <TableCell className="text-right font-data">{r.brought_forward}</TableCell>
                    <TableCell className="text-right font-data">{r.earned}</TableCell>
                    <TableCell className="text-right font-data">{r.availed}</TableCell>
                    <TableCell className="text-right font-data font-semibold">{r.balance}</TableCell>
                    <TableCell className="text-right font-data">{r.carry_forward ? r.to_carry_forward : "—"}</TableCell>
                    <TableCell>
                      {!readOnly && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" data-testid={`edit-alloc-${i}`} onClick={() => setEditAlloc({ ...r, balance_override: r.balance_override ?? "" })}
                            className="text-slate-400 hover:text-slate-950 hover:bg-slate-100">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" data-testid={`remove-alloc-${i}`} onClick={() => removeAlloc(r)}
                            className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={allocOpen} onOpenChange={setAllocOpen}>
            <DialogContent className="bg-white rounded-md" data-testid="allocate-leave-dialog">
              <DialogHeader><DialogTitle>Allocate leave to a teacher</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Teacher</Label>
                  <Select value={allocForm.teacher_id || ""} onValueChange={(v) => setAllocForm({ ...allocForm, teacher_id: v })}>
                    <SelectTrigger data-testid="alloc-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                    <SelectContent>
                      {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Leave type</Label>
                  <Select value={allocForm.leave_type_id || ""} onValueChange={(v) => setAllocForm({ ...allocForm, leave_type_id: v })}>
                    <SelectTrigger data-testid="alloc-type-select"><SelectValue placeholder="Select leave type" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Entitlement (blank = type default)</Label>
                    <Input type="number" step="0.5" min="0" data-testid="alloc-entitlement-input" value={allocForm.entitlement ?? ""}
                      onChange={(e) => setAllocForm({ ...allocForm, entitlement: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Brought forward</Label>
                    <Input type="number" step="0.5" min="0" data-testid="alloc-brought-input" value={allocForm.brought_forward ?? 0}
                      onChange={(e) => setAllocForm({ ...allocForm, brought_forward: e.target.value })} /></div>
                </div>
                <Button data-testid="alloc-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white"
                  onClick={() => saveAlloc({
                    teacher_id: allocForm.teacher_id, leave_type_id: allocForm.leave_type_id,
                    entitlement: allocForm.entitlement === "" || allocForm.entitlement == null ? null : Number(allocForm.entitlement),
                    brought_forward: Number(allocForm.brought_forward) || 0,
                    availed_extra: 0, balance_override: null,
                  })}>
                  Allocate
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={!!editAlloc} onOpenChange={() => setEditAlloc(null)}>
            <DialogContent className="bg-white rounded-md" data-testid="edit-alloc-dialog">
              <DialogHeader><DialogTitle>Edit — {editAlloc?.teacher_name} · {editAlloc?.leave_type}</DialogTitle></DialogHeader>
              {editAlloc && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Entitlement</Label>
                      <Input type="number" step="0.5" min="0" data-testid="edit-alloc-entitlement" value={editAlloc.entitlement}
                        onChange={(e) => setEditAlloc({ ...editAlloc, entitlement: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Brought forward</Label>
                      <Input type="number" step="0.5" min="0" data-testid="edit-alloc-brought" value={editAlloc.brought_forward}
                        onChange={(e) => setEditAlloc({ ...editAlloc, brought_forward: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Taken adjustment (adds to availed)</Label>
                      <Input type="number" step="0.5" data-testid="edit-alloc-availed-extra" value={editAlloc.availed_extra}
                        onChange={(e) => setEditAlloc({ ...editAlloc, availed_extra: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Balance override (blank = auto)</Label>
                      <Input type="number" step="0.5" data-testid="edit-alloc-balance-override" value={editAlloc.balance_override}
                        onChange={(e) => setEditAlloc({ ...editAlloc, balance_override: e.target.value })} /></div>
                  </div>
                  <Button data-testid="edit-alloc-save-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white"
                    onClick={() => saveAlloc({
                      teacher_id: editAlloc.teacher_id, leave_type_id: editAlloc.leave_type_id,
                      entitlement: editAlloc.entitlement === "" ? null : Number(editAlloc.entitlement),
                      brought_forward: Number(editAlloc.brought_forward) || 0,
                      availed_extra: Number(editAlloc.availed_extra) || 0,
                      balance_override: editAlloc.balance_override === "" ? null : Number(editAlloc.balance_override),
                    })}>
                    Save changes
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
